import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { Store } from '@ngxs/store';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';

import { environment } from '../environments/environment';

import { GAME_ID_KEY_FOR_ROUTE, PERIOD_KEY_FOR_QUERY_PARAM } from './app.utils';
import { IAppResolveData } from './app.models';
import { FetchGameForId, SetGameId } from './states/game/game.actions';
import { FetchPlayerEventDefinitions, FetchGameEventDefinitions, FetchShiftEventDefinitions, FetchFlagDefinitions, FetchGameEvents, FetchPlayerShiftEvents, FetchPlayerEvents } from './states/event/event.actions';
import { GameState } from './states/game/game.state';
import { FetchRosterForGame } from './states/roster/roster.actions';
import { FetchTeamsForLeague } from './states/team/team.actions';
import { isPresent } from './utils/type.utils';
import { extractErrorMessageStringFromHttpErrorResponse } from './utils/api.utils';
import { actionDispatchReturnError, isDefined } from './utils/operator.utils';
import { HideAppLoader, ShowAppLoader } from './app.actions';
import { FetchApplicationData } from './states/application/application.actions';
import { FetchVenue } from './states/venue/venue.actions';
import { EPlayerEventsMode } from './states/event/event.models';

export const AppResolve: ResolveFn<Observable<IAppResolveData>> = (route: ActivatedRouteSnapshot): Observable<IAppResolveData> => {
    const store = inject(Store);

    const gameId = route.paramMap.get(GAME_ID_KEY_FOR_ROUTE);
    const period = route.queryParamMap.get(PERIOD_KEY_FOR_QUERY_PARAM);

    const resolveData: IAppResolveData = { fatalErrors: [] };

    store.dispatch(new ShowAppLoader);

    if (isPresent(gameId)) {
        return store.dispatch(new SetGameId(gameId)).pipe(
            // dispatch the action to fetch the game
            switchMap(() => store.dispatch(new FetchGameForId(gameId, { withperiods: true, withvidparams: true, withscores: true }))),
            // retrieve the fetched game from the state
            switchMap(() => store.select(GameState.selectors.game)),
            isDefined(),
            // catch any errors from fetching the game
            catchError((err) => {
                resolveData.fatalErrors.push(
                    extractErrorMessageStringFromHttpErrorResponse(err)
                );

                // return undefined so things will continue in the next pipe
                return of(null);
            }),
            switchMap(game => {
                if (!game) {
                    // game was not found, return the resolve data which will contain whatever error came from the game endpoint
                    return of(resolveData);
                } else {
                    const periodNumber = period ? parseInt(period, 10) : 1;
                    const parallelDispatches = [
                        store.dispatch(new FetchTeamsForLeague(game.leagueId)).pipe(actionDispatchReturnError()),
                        store.dispatch(new FetchRosterForGame(game.id)).pipe(actionDispatchReturnError()),
                        store.dispatch(new FetchPlayerEventDefinitions).pipe(actionDispatchReturnError()),
                        store.dispatch(new FetchGameEventDefinitions()).pipe(actionDispatchReturnError()),
                        store.dispatch(new FetchShiftEventDefinitions()).pipe(actionDispatchReturnError()),
                        store.dispatch(new FetchFlagDefinitions()).pipe(actionDispatchReturnError()),
                        store.dispatch(new FetchApplicationData()).pipe(actionDispatchReturnError()),
                        store.dispatch(new FetchVenue(game.venueId)).pipe(actionDispatchReturnError()),
                        store.dispatch(new FetchGameEvents({
                            gameid: [game.id],
                            period: [periodNumber]
                        })).pipe(actionDispatchReturnError()),
                        store.dispatch(new FetchPlayerShiftEvents({
                            gameid: [game.id],
                            period: [periodNumber],
                        })).pipe(actionDispatchReturnError()),
                        store.dispatch(new FetchPlayerEvents({
                            gameid: [game.id],
                            mode: EPlayerEventsMode.RAW,
                            period: [periodNumber],
                        })).pipe(actionDispatchReturnError())
                    ];

                    return forkJoin(parallelDispatches).pipe(
                        map((results: (undefined | string)[]) => {
                            const errors = results.filter((r): r is string => r !== undefined);
                            resolveData.fatalErrors.push(...errors);
                            store.dispatch(new HideAppLoader());
                            return resolveData;
                        })
                    );
                }
            })
        );
    } else {
        // there is no game ID in the URL so we redirect them to the login page
        window.location.href = environment.loginUrl;
        return of(resolveData);
    }
};
